import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import {
	CustomDetailEnum,
	KycWorkflowCustomDetailResponseDto,
} from '@dealstack/shared/lib/generated';
import {
	getKycWorkflowCustomFields,
	updateKycWorkflowCustomField,
} from '@dealstack/frontend/shared/dataExchange/services/kyc-workflow-custom-detail.service.ts';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';

export const useKycWorkflowCustomFields = (kycWorkflowId: string | undefined) => {
	const tMipManage = useTranslationMipManage();
	const queryClient = useQueryClient();

	const queryKey = [API_PATHS.MIP_CUSTOM_DETAIL_KYCWORKFLOW_KYCWORKFLOWID_GET, kycWorkflowId];

	const { data, isLoading } = useQuery({
		queryKey,
		queryFn: () => getKycWorkflowCustomFields(kycWorkflowId ?? ''),
		enabled: !!kycWorkflowId,
		select: (response) => response.payload,
	});

	const updateMutation = useMutation({
		mutationFn: (variables: { customDetailId: string; isRequired: boolean }) =>
			updateKycWorkflowCustomField(kycWorkflowId ?? '', variables),
		onSuccess: (_response, variables) => {
			queryClient.setQueryData(
				queryKey,
				(oldData: { payload: KycWorkflowCustomDetailResponseDto[] } | undefined) => {
					if (!oldData?.payload) {
						return oldData;
					}
					return {
						...oldData,
						payload: oldData.payload.map((field) =>
							field.customDetailId === variables.customDetailId
								? { ...field, isRequired: variables.isRequired }
								: field
						),
					};
				}
			);
		},
		onError: () => {
			showToastV2({
				state: 'error',
				title: tMipManage('investor_data.fields.updated_error'),
			});
		},
	});

	const customFields = useMemo(() => data ?? [], [data]);

	const individualCustomFields = useMemo(
		() => customFields.filter((field) => field.type === CustomDetailEnum.INDIVIDUAL),
		[customFields]
	);

	const vehicleCustomFields = useMemo(
		() => customFields.filter((field) => field.type === CustomDetailEnum.VEHICLE),
		[customFields]
	);

	const toggleCustomField = (customDetailId: string, currentIsRequired: boolean) => {
		if (!kycWorkflowId || updateMutation.isPending) {
			return;
		}
		updateMutation.mutate({ customDetailId, isRequired: !currentIsRequired });
	};

	return {
		individualCustomFields,
		vehicleCustomFields,
		toggleCustomField,
		isLoading,
	};
};
